// /src/api/items.js
import { apiGet, apiPost, apiPatch } from "lib/apiClient";

/**
 * ЕДИНЫЙ ФОРМАТ ТОВАРА:
 * {
 *   id, sku, name, unit,
 *   price,
 *   last_price,         // = Number(price)
 *   min_stock,
 *   category_id, category_name,
 *   default_supplier,
 *   is_archived,
 *   qty_on_hand,        // всегда число >= 0
 *   created_at, updated_at
 * }
 */

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toSkuOrNull = (v) => {
  const s = String(v ?? "").trim();
  return s || null;
};

const toNullableTrimmed = (v) => {
  const s = v == null ? "" : String(v).trim();
  return s ? s : null;
};

function normalizeItemShape(r) {
  const qty = Math.max(0, toNumber(r.qty_on_hand));
  const price = toNumber(r.price);
  return {
    id: r.id,
    sku: r.sku,
    name: r.name,
    unit: r.unit,
    price,
    last_price: price,
    min_stock: toNumber(r.min_stock),
    category_id: r.category_id,
    category_name: r.category_name ?? null,
    supplier_id: r.supplier_id ?? null,
    supplier_name: r.supplier_name ?? null,
    is_archived: !!r.is_archived,
    is_consumable: !!r.is_consumable,
    qty_on_hand: qty,
    created_at: r.created_at ?? null,
    updated_at: r.updated_at ?? null,
  };
}

/* ──────────────────────────────────────────────────────────────
 * КАТЕГОРИИ
 * ────────────────────────────────────────────────────────────── */
export async function getCategories() {
  const { data, error } = await apiGet("/categories/");
  if (error) throw new Error(error);
  return (data || []).map((r) => ({ id: r.id, name: r.name }));
}

export async function createCategory({ name }) {
  const n = String(name ?? "").trim();
  if (!n) throw new Error("Укажите имя категории");
  const { data, error } = await apiPost("/categories/", { name: n });
  if (error) throw new Error(error);
  return { id: data.id, name: data.name };
}

/* ──────────────────────────────────────────────────────────────
 * ТОВАРЫ — СПИСОК, ОДИН, CRUD
 * ────────────────────────────────────────────────────────────── */
export async function listItems({
  q = "",
  category_id = "",
  show_archived = false,
  limit = 20,
  offset = 0,
} = {}) {
  const params = {
    limit,
    offset,
  };

  if (!show_archived) params.show_archived = false;
  if (category_id) params.category_id = category_id;
  if (q) params.q = q;

  const { data, error } = await apiGet("/items/", params);
  if (error) throw new Error(error);

  const rows = (data.rows || []).map(normalizeItemShape);
  return { rows, count: data.count ?? rows.length };
}

export async function getItem(id) {
  const { data, error } = await apiGet(`/items/${id}/`);
  if (error) throw new Error(error);
  return normalizeItemShape(data);
}

export async function createItem({
  sku = null,
  name,
  category_id = null,
  unit = "шт",
  min_stock = 0,
  price = 0,
  default_supplier = null,
  supplier_id = null,
  is_consumable = false,
}) {
  const payload = {
    sku: toSkuOrNull(sku),
    name: String(name ?? "").trim(),
    category_id: category_id ? Number(category_id) : null,
    unit: String(unit ?? "шт").trim() || "шт",
    min_stock: toNumber(min_stock),
    price: toNumber(price),
    // default_supplier removed
    supplier_id: supplier_id ? Number(supplier_id) : null, // Use supplier_id
    is_consumable: !!is_consumable,
  };
  if (!payload.name) throw new Error("Укажите наименование товара");

  const { data, error } = await apiPost("/items/", payload);
  if (error) throw new Error(error);
  return normalizeItemShape(data);
}

export async function updateItem(id, patch = {}) {
  if (!id) throw new Error("Не указан ID товара");

  const allowed = [
    "sku",
    "name",
    "category_id",
    "unit",
    "min_stock",
    "price",
    // "default_supplier" removed
    "supplier_id",
    "is_archived",
    "is_consumable",
  ];
  const p = {};
  for (const k of allowed) {
    if (patch[k] !== undefined) p[k] = patch[k];
  }

  if (p.sku !== undefined) p.sku = toSkuOrNull(p.sku);
  if (p.name !== undefined) {
    p.name = String(p.name ?? "").trim();
    if (!p.name) throw new Error("Наименование не может быть пустым");
  }
  if (p.unit !== undefined) p.unit = String(p.unit ?? "шт").trim() || "шт";

  // Обрабатываем category_id
  if (p.category_id !== undefined) {
    if (p.category_id === null || p.category_id === "" || p.category_id === 0) {
      p.category_id = null;
    } else {
      const num = Number(p.category_id);
      if (!Number.isNaN(num) && num > 0) {
        p.category_id = num;
      } else {
        p.category_id = null;
      }
    }
  }

  // Обрабатываем supplier_id
  if (p.supplier_id !== undefined) {
    if (p.supplier_id === null || p.supplier_id === "" || p.supplier_id === 0) {
      p.supplier_id = null;
    } else {
      const num = Number(p.supplier_id);
      if (!Number.isNaN(num) && num > 0) {
        p.supplier_id = num;
      } else {
        p.supplier_id = null;
      }
    }
  }

  if (p.min_stock !== undefined && p.min_stock !== null) {
    p.min_stock = toNumber(p.min_stock);
  }
  if (p.price !== undefined && p.price !== null) {
    p.price = toNumber(p.price);
  }
  // default_supplier removed
  if (p.is_archived !== undefined) p.is_archived = !!p.is_archived;
  if (p.is_consumable !== undefined) p.is_consumable = !!p.is_consumable;

  // DEBUG ALERT REMOVED
  // window.alert(`Debug UpdateItem Payload: ${JSON.stringify(p)}`);

  const { data, error } = await apiPatch(`/items/${id}/`, p);
  if (error) throw new Error(error);
  return normalizeItemShape(data);
}

export async function toggleArchive({ id, archived }) {
  if (id === undefined || id === null) throw new Error("Не указан ID товара");
  const { data, error } = await apiPatch(`/items/${id}/`, { is_archived: !!archived });
  if (error) throw new Error(error);
  return data;
}

/* ──────────────────────────────────────────────────────────────
 * ПРИХОД (АКТ)
 * ────────────────────────────────────────────────────────────── */
export async function createIncomingAct({ counterparty, positions }) {
  const { data, error } = await apiPost("/incoming-act/", {
    counterparty: counterparty || null,
    positions: positions || [],
  });
  if (error) throw new Error(error);
  return { act_id: data.act_id };
}

/* ──────────────────────────────────────────────────────────────
 * ДЛЯ МОДАЛКИ ВЫДАЧИ (AssignModal)
 * ────────────────────────────────────────────────────────────── */
export async function getItemsForAssign() {
  const { data, error } = await apiGet("/items/for_assign/");
  if (error) throw new Error(error);
  return data || [];
}
